import { InputLabel, Select, FormControl } from "@material-ui/core";
import { useSelector } from "@xstate/store/react";
import { isNil } from "lodash";
import { usePage } from "@inertiajs/inertia-react";

import { ShoppingCart } from "./ShoppingCart";
import {
    useShoppingCartStore,
    useSubtotalPrice,
    useTotalPrice,
    useVatPrice,
} from "../stores/ShoppingCartStore";
import { useTransportProducts } from "../hooks/useTransportProducts";
import { Trans } from "../utils/i18n";
import { formatCurrency } from "../utils/formatCurrency";

// "variant" can be "default" or "minimal"
// "default" will show the full shopping cart with all products, transport and handling fee
// "minimal" will show only the total price and a button to open the full shopping cart
export function ShoppingCartContainer({ variant = "default" }) {
    const store = useShoppingCartStore();
    const { maxQuantity = 20 } = usePage().props;
    const products = useSelector(store, (state) => state.context.products);
    const transportProduct = useSelector(
        store,
        (state) => state.context.transportProduct
    );
    const handlingFeeProduct = useSelector(
        store,
        (state) => state.context.handlingFeeProduct
    );
    const subtotal = useSubtotalPrice();
    const vat = useVatPrice();
    const total = useTotalPrice();
    const { data: transportProductsData } = useTransportProducts({
        products: products.map((product) => ({
            id: product.id,
            amount: Number(product.amount ?? 1),
        })),
        onSuccess: (data) => {
            // Set the handling fee product if it is available
            if (data?.handling) {
                store.send({
                    type: "setHandlingFeeProduct",
                    payload: data?.handling,
                });
            }

            // If there are no transport products available due to amount changes, we need to reset the transport product.
            if (
                transportProduct &&
                (!data?.transport || data?.transport?.length === 0)
            ) {
                store.send({
                    type: "setTransportProduct",
                    payload: null,
                    source: "cart", // For emitting the event
                });
            }

            // If the transport product is not set yet, we set it as default to the first transport product
            if (!transportProduct && data?.transport?.[0]?.id) {
                store.send({
                    type: "setTransportProduct",
                    payload: data?.transport?.[0],
                    source: "cart", // For emitting the event
                });
            }
        },
    });

    return (
        <ShoppingCart.Root>
            {products.map((product) => (
                <ShoppingCart.ProductRow
                    showNetPrice={variant === "default"}
                    netPriceTotalLabel={variant === "default" ? undefined : <Trans>Price</Trans>}
                    key={product.id}
                    image={product.image}
                    title={product.description}
                    quantity={product.amount}
                    maxQuantity={maxQuantity || product.stockQuantity || product.stock_quantity}
                    price={product.price}
                    showImage={true}
                    showQuantity={true}
                    showRemoveButton={true}
                    onRemove={() =>
                        store.send({
                            type: "removeProduct",
                            payload: {
                                id: product.id,
                            },
                        })
                    }
                    onUpdateQuantity={(amount) =>
                        store.send({
                            type: "updateProduct",
                            payload: {
                                id: product.id,
                                data: {
                                    amount,
                                },
                            },
                        })
                    }
                />
            ))}

            {handlingFeeProduct && (
                <ShoppingCart.ProductRow
                    showNetPrice={variant === "default"}
                    netPriceTotalLabel={variant === "default" ? undefined : <Trans>Price</Trans>}
                    title={handlingFeeProduct.description}
                    price={handlingFeeProduct.price}
                    quantity={1}
                    showImage={false}
                    showQuantity={false}
                    showRemoveButton={false}
                />
            )}

            {transportProduct && (
                <ShoppingCart.ProductRow
                    showNetPrice={variant === "default"}
                    netPriceTotalLabel={variant === "default" ? undefined : <Trans>Price</Trans>}
                    title={
                        <FormControl variant="outlined" size="small">
                            <InputLabel id="transport-label">
                                <Trans i18nKey="common.transport">
                                    Transport
                                </Trans>
                            </InputLabel>
                            <Select
                                className="w-full"
                                labelId="transport-label"
                                label={
                                    <Trans i18nKey="common.transport">
                                        Transport
                                    </Trans>
                                }
                                size="small"
                                onChange={(e) => {
                                    const transportProduct =
                                        transportProductsData?.transport?.find(
                                            (product) =>
                                                product.id == e.target.value
                                        );

                                    if (!transportProduct) return;

                                    store.send({
                                        type: "setTransportProduct",
                                        payload: transportProduct,
                                        source: "cart", // For emitting the event
                                    });
                                }}
                                value={transportProduct?.id}
                                key={transportProduct?.id}
                                native
                            >
                                {transportProductsData?.transport?.map(
                                    (product) => (
                                        <option
                                            key={product.id}
                                            value={product.id}
                                        >
                                            {product.description}
                                        </option>
                                    )
                                ) ?? []}
                            </Select>
                        </FormControl>
                    }
                    price={transportProduct.price}
                    quantity={1}
                    showImage={false}
                    showQuantity={false}
                    showRemoveButton={false}
                />
            )}

            <ShoppingCart.PriceSummaryRow
                title={<Trans i18nKey="common.subtotal">Subtotal</Trans>}
                price={formatCurrency(
                    subtotal,
                    transportProduct?.price?.currency ?? "EUR"
                )}
            />

            <ShoppingCart.PriceSummaryRow
                title={
                    <>
                        <Trans i18nKey="common.vat">VAT</Trans>{" "}
                        {!isNil(transportProduct?.price?.vat) &&
                            `(${transportProduct?.price?.vat / 100}%)`}
                    </>
                }
                price={formatCurrency(
                    vat,
                    transportProduct?.price?.currency ?? "EUR"
                )}
            />

            <ShoppingCart.PriceSummaryRow
                title={
                    <span className="font-bold">
                        <Trans>Total</Trans>
                    </span>
                }
                price={formatCurrency(
                    total,
                    transportProduct?.price?.currency ?? "EUR"
                )}
            />
        </ShoppingCart.Root>
    );
}